import { ControlledRadio } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { ProjectMethodology } from 'components/ProjectMethodology';
import { RequestStatus } from 'features/request/requestSlice';
import type { CatalogTypeData, ProjectMethodologyEnum } from 'gql/graphql';
import { methodologyLabels } from 'types/methodology';
import type { TextFieldOnChangeArguments } from 'uikit';
import { cnMixSpace, HeaderSearchBar } from 'uikit';

import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import type { Control } from 'react-hook-form';

import Styles from './CreateCatalog.module.css';

type CatalogTypeItem = CatalogTypeData & { disabled?: boolean };

export const CreateCatalogDialog = ({
    name,
    control,
    catalogTypes = [],
    getTypesStatus,
    createCatalogStatus,
    onCloseHandler,
    onConfirmHandler,
    isConfirmDisabled,
}: {
    name: string;
    control: Control;
    catalogTypes: CatalogTypeItem[] | undefined;
    getTypesStatus: RequestStatus;
    createCatalogStatus: RequestStatus;
    onCloseHandler: () => void;
    onConfirmHandler: () => void;
    isConfirmDisabled?: boolean;
}) => {
    const isLoading = getTypesStatus === RequestStatus.FETCHING;
    const isCommit = createCatalogStatus === RequestStatus.FETCHING;

    const [searchString, setSearchString] = useState('');

    const handleChangeSearchString = useCallback((args: TextFieldOnChangeArguments) => {
        const { value } = args;
        const newString = (value ?? '').toLowerCase();

        setSearchString(newString);
    }, []);

    const visibleItems = useMemo(
        () =>
            catalogTypes?.filter(
                (catalogType) =>
                    catalogType.name.toLowerCase().includes(searchString) ||
                    catalogType.methodologies.some((method) =>
                        methodologyLabels[method].toLowerCase().includes(searchString),
                    ),
            ),
        [catalogTypes, searchString],
    );

    return (
        <ModalDialog
            isOpen
            isOnClickOutsideDisable
            withTitleCloseButton
            title="Создание Справочника"
            onClose={onCloseHandler}
            onConfirm={onConfirmHandler}
            bodyWidth={600}
            isLoading={isLoading}
            isCommit={isCommit}
            btnConfirmDisabled={isConfirmDisabled}
            btnConfirmLabel="Создать"
            className={Styles.modal}
        >
            <div className={cn(Styles.container, cnMixSpace({ mH: 'l', mV: 'l' }))}>
                {handleChangeSearchString && (
                    <HeaderSearchBar
                        placeholder="Введите наименование справочника или методологии"
                        label="поиск"
                        value={searchString}
                        onChange={handleChangeSearchString}
                        className={Styles.searchBar}
                    />
                )}
                <ControlledRadio
                    name={name}
                    control={control}
                    items={visibleItems as any}
                    getItemLabel={(item: any) =>
                        (
                            <div className={Styles.radioItem}>
                                {item.name}

                                <div>
                                    {item.methodologies.map((methodology: ProjectMethodologyEnum) => (
                                        <span key={methodology} className={cnMixSpace({ mR: 'xs' })}>
                                            <ProjectMethodology key={methodology} methodology={methodology} />
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ) as any
                    }
                    getItemKey={(item: any) => item.uniqueId}
                    className={Styles.createCatalog}
                />
            </div>
        </ModalDialog>
    );
};

CreateCatalogDialog.displayName = 'CreateCatalogDialog';
